import { useCallback, useEffect, useState } from "react";
import { Mail, Filter, RefreshCw } from "lucide-react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";
import {
  listEmailLogs,
  type EmailLogEntry,
  type EmailLogFilters,
} from "@/api/emailLogs";

/**
 * Email delivery log (HR admin).
 *
 * Cursor-paginated read of the new ``email_logs`` table. Helps HR debug
 * "why didn't my team get that invitation" without grepping server logs.
 */
export function EmailLogSettings() {
  const { addToast } = useToast();

  const [statusInput, setStatusInput] = useState("");
  const [toEmailInput, setToEmailInput] = useState("");
  const [templateInput, setTemplateInput] = useState("");
  const [applied, setApplied] = useState<EmailLogFilters>({});

  const [items, setItems] = useState<EmailLogEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const fetchPage = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      try {
        const filters: EmailLogFilters = { ...applied, limit: 50 };
        if (!reset && cursor) filters.before = cursor;
        const page = await listEmailLogs(filters);
        setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
        setCursor(page.next_before);
        setHasMore(page.has_more);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : "Failed to load email log";
        addToast("error", msg);
      } finally {
        setLoading(false);
      }
    },
    [applied, cursor, addToast],
  );

  useEffect(() => {
    void fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  const onApply = () => {
    const next: EmailLogFilters = {};
    if (statusInput) next.status = statusInput;
    if (toEmailInput.trim()) next.to_email = toEmailInput.trim();
    if (templateInput.trim()) next.template_key = templateInput.trim();
    setApplied(next);
  };

  const onClear = () => {
    setStatusInput("");
    setToEmailInput("");
    setTemplateInput("");
    setApplied({});
  };

  const statusVariant = (s: string): "success" | "warning" | "danger" | "default" => {
    if (s === "sent") return "success";
    if (s === "suppressed") return "warning";
    if (s === "failed") return "danger";
    return "default";
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email delivery log
        </CardTitle>
      </CardHeader>

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium">Status</label>
            <select
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <option value="">Any</option>
              <option value="sent">Sent</option>
              <option value="suppressed">Suppressed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Recipient contains</label>
            <Input
              value={toEmailInput}
              placeholder="anna@"
              onChange={(e) => setToEmailInput(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium">Template key</label>
            <Input
              value={templateInput}
              placeholder="invoice.reminder"
              onChange={(e) => setTemplateInput(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={onApply} disabled={loading}>
            <Filter className="mr-2 h-4 w-4" /> Apply
          </Button>
          <Button variant="ghost" onClick={onClear} disabled={loading}>
            Clear
          </Button>
          <Button
            variant="ghost"
            onClick={() => void fetchPage(true)}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-3 py-2">Sent</th>
                <th className="px-3 py-2">To</th>
                <th className="px-3 py-2">Subject</th>
                <th className="px-3 py-2">Template</th>
                <th className="px-3 py-2">Backend</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr>
                  <td
                    className="px-3 py-6 text-center text-muted-foreground"
                    colSpan={6}
                  >
                    No email log entries.
                  </td>
                </tr>
              )}
              {items.map((row) => {
                const isOpen = !!expanded[row.id];
                return (
                  <tr key={row.id} className="border-t align-top">
                    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">
                      {new Date(row.sent_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs break-all">
                      {row.to_email}
                    </td>
                    <td className="px-3 py-2">
                      <div>{row.subject}</div>
                      {row.status === "failed" && row.error_message && (
                        <button
                          type="button"
                          className="mt-1 text-xs text-danger underline"
                          onClick={() =>
                            setExpanded((prev) => ({
                              ...prev,
                              [row.id]: !prev[row.id],
                            }))
                          }
                        >
                          {isOpen ? "Hide error" : "Show error"}
                        </button>
                      )}
                      {isOpen && row.error_message && (
                        <pre className="mt-2 max-w-xl overflow-auto rounded bg-muted/40 p-2 text-xs">
{row.error_message}
                        </pre>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {row.template_key || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {row.backend || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={statusVariant(row.status)}>
                        {row.status}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            Showing {items.length} entr{items.length === 1 ? "y" : "ies"}.
          </div>
          <Button
            variant="ghost"
            onClick={() => void fetchPage(false)}
            disabled={!hasMore || loading}
          >
            {loading ? "Loading…" : hasMore ? "Load more" : "End of log"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
